import Image from "next/image";

import StarIcon from "@repo/ui-system/icons/rating/star/filled.svg";
import { Dialog } from "radix-ui";

import { type OutboundBiParams } from "@/lib/affiliate/build-redirect-url";

import { OutboundLink } from "../../../outbound-link/outbound-link";
import { LimitedTimeDealStrip } from "../limited-time-deal";

export type PopupContent = {
  badge: string;
  discountLabel: string | null;
  imageUrl: string;
  title: string;
  reviewCount: string | null;
  ctaLabel: string;
  ctaUrl: string;
  ctaStoreName: string;
  ctaBi: OutboundBiParams;
  storeName: string;
  storeLogo: string | null;
  limitedTimeDealSeconds: number | null;
};

type Props = {
  content: PopupContent;
};

export function PromotionPopupContent({ content }: Props) {
  return (
    <OutboundLink
      url={content.ctaUrl}
      storeName={content.ctaStoreName}
      bi={content.ctaBi}
      target="_blank"
      rel="noopener noreferrer"
    >
      <div className="relative z-10 flex flex-col items-center gap-6 pb-4 pt-3 lg:pt-8 lg:pb-6 lg:px-20 bg-[linear-gradient(8deg,#FFF_47.41%,#F0F3FF_95.27%)] rounded-2xl">
        {/* Badges + Image */}
        <div className="flex flex-col items-center w-full">
          <div className="flex flex-col gap-2 items-start mb-[-28px] px-3 w-full z-10 lg:absolute lg:left-6 lg:top-6 lg:mb-0 lg:px-0 lg:w-auto">
            <Dialog.Title asChild>
              <span
                className="bg-badge-first-bg text-badge-first-text px-2 py-1 text-base font-semibold leading-6"
                style={{ borderRadius: "8px" }}
              >
                {content.badge}
              </span>
            </Dialog.Title>
            {content.discountLabel && (
              <span
                className="bg-badge-sale-bg text-badge-sale-text px-2 py-1 text-sm font-medium leading-5"
                style={{ borderRadius: "8px" }}
              >
                {content.discountLabel}
              </span>
            )}
          </div>
          <div className="size-[140px] mix-blend-darken">
            <Image
              src={content.imageUrl}
              alt={content.title}
              width={140}
              height={140}
              sizes="140px"
              className="size-[140px] object-contain"
            />
          </div>
        </div>

        {/* Reviews + Title */}
        <div className="flex flex-col gap-5 items-start w-full">
          <div className="flex flex-col gap-4 items-start px-6 lg:px-0 w-full">
            {content.reviewCount && (
              <div className="flex gap-2 items-center px-2 py-0.5 bg-surface-primary-subtle rounded-[6px]">
                <StarIcon
                  className="size-4 text-elements-random-yellow"
                  aria-hidden="true"
                />
                <span className="text-sm lg:text-base font-medium leading-5 lg:leading-6 text-text-grayscale-caption">
                  Based on {content.reviewCount} Reviews
                </span>
              </div>
            )}
            <p className="text-sm lg:text-base font-medium leading-5 lg:leading-6 line-clamp-2 text-text-grayscale-subtitle">
              {content.title}
            </p>
          </div>

          {/* CTA visual (not a nested link) */}
          <div className="flex flex-col gap-4 items-start px-6 lg:px-0 w-full">
            <span className="flex items-center justify-center w-full bg-cta lg:hover:bg-cta-hover lg:transition-colors text-grayscale-white font-semibold text-base rounded-xl py-2.5 px-5">
              {content.ctaLabel}
            </span>
            <div className="flex items-center justify-center w-full h-5">
              {content.storeLogo ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={content.storeLogo}
                  alt={content.storeName}
                  className="h-5 w-auto object-contain"
                />
              ) : (
                <span
                  className="text-sm"
                  style={{ color: "#505667" }}
                >
                  {content.storeName}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {content.limitedTimeDealSeconds != null && (
        <div className="relative -mt-3 flex items-center justify-center bg-badge-timedeal-bg pt-3 before:absolute before:top-0 before:left-0 before:size-3 before:bg-[radial-gradient(circle_at_0_0,transparent_12px,var(--color-badge-timedeal-bg)_12px)] after:absolute after:top-0 after:right-0 after:size-3 after:bg-[radial-gradient(circle_at_100%_0,transparent_12px,var(--color-badge-timedeal-bg)_12px)]">
          <LimitedTimeDealStrip
            initialSeconds={content.limitedTimeDealSeconds}
          />
        </div>
      )}
    </OutboundLink>
  );
}
